import React, { useState } from 'react';
import { string, object, bool } from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from '../../util/reactIntl';
import css from './WaitlistButton.module.css';

/**
 * WaitlistButton — let users join/leave a waitlist for a fully-booked listing.
 *
 * Usage (in OrderPanel or listing page when no availability):
 *   <WaitlistButton listing={currentListing} currentUser={currentUser} />
 *
 * Waitlist is stored in listing.attributes.metadata.waitlist[] as user UUIDs.
 */
const WaitlistButton = ({ listing, currentUser, className }) => {
  const listingId = listing?.id?.uuid;
  const userId = currentUser?.id?.uuid;

  const waitlist = listing?.attributes?.metadata?.waitlist || [];
  const [isOnWaitlist, setIsOnWaitlist] = useState(userId ? waitlist.includes(userId) : false);
  const [isLoading, setIsLoading] = useState(false);
  const [count, setCount] = useState(waitlist.length);

  if (!userId) {
    return (
      <div className={classNames(css.root, className)}>
        <p className={css.notice}>
          <FormattedMessage id="WaitlistButton.loginRequired" />
        </p>
      </div>
    );
  }

  const toggle = async () => {
    if (!listingId || isLoading) return;

    setIsLoading(true);
    try {
      const method = isOnWaitlist ? 'DELETE' : 'POST';
      const endpoint = isOnWaitlist ? '/api/waitlist/leave' : '/api/waitlist/join';

      const res = await fetch(endpoint, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ listingId }),
      });

      if (res.ok) {
        const data = await res.json();
        setIsOnWaitlist(!isOnWaitlist);
        setCount(data.waitlist?.length ?? count);
      }
    } catch (err) {
      console.error('[WaitlistButton] error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={classNames(css.root, className)}>
      <button
        type="button"
        onClick={toggle}
        disabled={isLoading}
        className={classNames(css.button, isOnWaitlist && css.buttonActive)}
      >
        {isLoading ? (
          <FormattedMessage id="WaitlistButton.loading" />
        ) : isOnWaitlist ? (
          <FormattedMessage id="WaitlistButton.leave" />
        ) : (
          <FormattedMessage id="WaitlistButton.join" />
        )}
      </button>
      {isOnWaitlist && (
        <p className={css.notice}>
          <FormattedMessage id="WaitlistButton.position" values={{ count }} />
        </p>
      )}
    </div>
  );
};

WaitlistButton.defaultProps = {
  className: null,
  currentUser: null,
};

WaitlistButton.propTypes = {
  listing: object.isRequired,
  currentUser: object,
  className: string,
};

export default WaitlistButton;
